import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FormInputProps } from '@microservices/wiskey-react-components'
import { DoneAll } from '@mui/icons-material'
import { MenuItem, Typography } from '@mui/material'

import { ETimeUnit } from '@hooks'
import { getObjectValueName } from '@helpers'
import {
  FIELD_VALUE_TYPE,
  FORMATS_DATE_OPTIONS,
  GENERATOR_INPUT_TYPE,
  MAX_INPUT_VALUE_MINUTES,
  REGEX,
  SEARCH_FIELD_TYPE,
} from '@constants'
import { AutocompleteOption, ObjectFieldDTO, ViewShortDTO } from '@types'
import { ConstFormat } from '@formats'

type ValueInputsOptions = {
  objectFields: ObjectFieldDTO[] | undefined
  linkedObjectFields: ObjectFieldDTO[] | undefined
  valueNameInputName: string
  defaultPlaceholderInputName: string
  placeholderValueInputName: string
  fieldTypeInputName: string
  formatDateInputName: string
  objectValueInputName: string
  watchedObject: AutocompleteOption | null
  watchedValueName: AutocompleteOption | null
  placeholderStartInputName: string
  placeholderEndInputName: string
  watchedDefaultplaceholder: boolean
  dropDownListCodeInputName: string
  dropDownWindowInputName: string
  dropdownEntities: ViewShortDTO[]
  valueType?: FIELD_VALUE_TYPE
  asCheckboxInputName: string
  asDurationInputName: string
  valueTypeInputName: string
  timeIncrementInputName: string
  valueMinName: string
  valueMaxName: string
  timeUnitName: string
  watchedMin?: number
  watchedMax?: number
  watchedValue?: any
}

type AutocompleteOptionValueType = AutocompleteOption & { enumLabel?: string }

export const useValueInputs = ({
  objectFields,
  linkedObjectFields,
  valueNameInputName,
  fieldTypeInputName,
  formatDateInputName,
  objectValueInputName,
  watchedValueName,
  defaultPlaceholderInputName,
  placeholderValueInputName,
  placeholderStartInputName,
  placeholderEndInputName,
  watchedDefaultplaceholder,
  dropDownListCodeInputName,
  dropDownWindowInputName,
  asCheckboxInputName,
  asDurationInputName,
  dropdownEntities,
  valueTypeInputName,
  valueType,
  timeIncrementInputName,
  valueMaxName,
  valueMinName,
  timeUnitName,
  watchedMin,
  watchedMax,
  watchedValue,
  watchedObject,
}: ValueInputsOptions) => {
  const { t } = useTranslation()

  const dropdownEntityOptions: AutocompleteOption[] = useMemo(
    () =>
      dropdownEntities.map(entity => ({
        id: entity.code,
        label: entity.title,
      })) ?? [],
    [dropdownEntities]
  )

  const valueInput = useMemo((): FormInputProps => {
    const options = [...(objectFields || [])].map(field => ({
      id: field.model ?? field.id,
      label: field.name,
      labelWithPostfix: getObjectValueName(field.name, field.type, field.isPk),
    }))

    return {
      name: valueNameInputName,
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      getOptionLabel: (option: AutocompleteOptionValueType) => option.label,
      renderAutocompleteOption: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: { id: string | number; label: string }
      ) => {
        const currentObjectField = objectFields?.find(field => field.name === option.label)

        if (!currentObjectField) {
          return <MenuItem>{option.label}</MenuItem>
        }

        return (
          <MenuItem {...props}>
            <Typography ml={1}>
              {getObjectValueName(
                currentObjectField.name,
                currentObjectField.type,
                currentObjectField.isPk
              )}
            </Typography>
          </MenuItem>
        )
      },
      label: t('searchFilterForm.value.label'),
      placeholder: t('searchFilterForm.value.placeholder'),
      isOptionEqualToValue: (option, value) => option.label === value.label,
      disabled: !watchedObject?.id,
      autocompleteOptions: options,
      showTitleFromLabelWithPostfix: true,
      rules: {
        required: true,
      },
    }
  }, [objectFields, valueNameInputName, watchedValueName, watchedObject, t])

  const fieldTypeInput: FormInputProps = {
    name: fieldTypeInputName,
    inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
    getOptionLabel: option => (option.label ? option.label : ''),
    label: t('searchFilterForm.fieldType.label'),
    placeholder: t('searchFilterForm.fieldType.placeholder'),
    isOptionEqualToValue: (option, value) => option.label === value.label,
    autocompleteOptions: Object.values(SEARCH_FIELD_TYPE).map(value => ({
      id: value,
      label: value,
    })),
    rules: {
      required: Boolean(watchedValue),
      validate: value => value !== t('searchFilterForm.fieldType.placeholder'),
    },
  }

  const formatDateInput: FormInputProps = useMemo(
    () => ({
      name: formatDateInputName,
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      placeholder: t('searchFilterForm.formatDate.placeholder'),
      label: t('searchFilterForm.formatDate.label'),
      autocompleteOptions: FORMATS_DATE_OPTIONS,
    }),
    [formatDateInputName, t]
  )

  const timeIncrementInput: FormInputProps = {
    name: timeIncrementInputName,
    inputType: GENERATOR_INPUT_TYPE.INPUT,
    label: t('searchFilterForm.timeIncrement.label'),
    placeholder: t('searchFilterForm.timeIncrement.placeholder'),
    textFieldInputProps: {
      inputComponent: ConstFormat as never,
      inputProps: { min: 0, max: MAX_INPUT_VALUE_MINUTES },
    },
  }

  const minInput: FormInputProps = {
    name: valueMinName,
    inputType: GENERATOR_INPUT_TYPE.INPUT,
    label: t('searchFilterForm.min.label'),
    placeholder: t('searchFilterForm.min.placeholder'),
    textFieldInputProps: {
      inputProps: { type: 'number' },
    },
    rules: {
      required: true,
      pattern: REGEX.NUMBER_REGEX,
      validate: value => {
        if (watchedMax !== undefined && Number(value) >= Number(watchedMax)) {
          return t('searchFilterForm.min.validation')
        }

        return true
      },
    },
  }

  const maxInput: FormInputProps = {
    name: valueMaxName,
    inputType: GENERATOR_INPUT_TYPE.INPUT,
    label: t('searchFilterForm.max.label'),
    placeholder: t('searchFilterForm.max.placeholder'),
    textFieldInputProps: {
      inputProps: { type: 'number' },
    },
    rules: {
      required: true,
      pattern: REGEX.NUMBER_REGEX,
      validate: value => {
        if (watchedMin !== undefined && Number(value) <= Number(watchedMin)) {
          return t('searchFilterForm.max.validation')
        }

        return true
      },
    },
  }

  const placeholderValueInput: FormInputProps = useMemo(
    () => ({
      name: placeholderValueInputName,
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      label: t('searchFilterForm.placeholderValue.label'),
      placeholder: t('searchFilterForm.placeholderValue.placeholder'),
      disabled: watchedDefaultplaceholder,
    }),
    [placeholderValueInputName, watchedDefaultplaceholder, t]
  )

  const placeholderStartInput: FormInputProps = useMemo(
    () => ({
      name: placeholderStartInputName,
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      label: t('searchFilterForm.placeholderStart.label'),
      placeholder: t('searchFilterForm.placeholderStart.placeholder'),
      disabled: watchedDefaultplaceholder,
    }),
    [placeholderStartInputName, watchedDefaultplaceholder, t]
  )

  const placeholderEndInput: FormInputProps = useMemo(
    () => ({
      name: placeholderEndInputName,
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      label: t('searchFilterForm.placeholderEnd.label'),
      placeholder: t('searchFilterForm.placeholderEnd.placeholder'),
      disabled: watchedDefaultplaceholder,
    }),
    [placeholderEndInputName, watchedDefaultplaceholder, t]
  )

  const defaultPlaceholderInput: FormInputProps = useMemo(
    () => ({
      name: defaultPlaceholderInputName,
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: t('searchFilterForm.defaultPlaceholder.label'),
      labelPlacement: 'end',
    }),
    [defaultPlaceholderInputName, t]
  )

  const dropDownListInput: FormInputProps = useMemo(
    () => ({
      name: dropDownListCodeInputName,
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      placeholder: t('placeholder.dropDownList'),
      label: t('label.dropDownList'),
      autocompleteOptions: dropdownEntityOptions,
    }),
    [dropDownListCodeInputName, dropdownEntityOptions, t]
  )

  const dropDownWindowInput: FormInputProps = useMemo(
    () => ({
      name: dropDownWindowInputName,
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      placeholder: t('placeholder.dropDownWindow'),
      label: t('label.dropDownWindow'),
      autocompleteOptions: dropdownEntityOptions,
    }),
    [dropDownWindowInputName, dropdownEntityOptions, t]
  )

  const valueTypeInput: FormInputProps = useMemo(
    () => ({
      name: valueTypeInputName,
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      label: t('label.valueType'),
      placeholder: t('placeholder.valueType'),
      disabled: true,
      value: valueType,
    }),
    [valueTypeInputName, valueType, t]
  )

  const asCheckboxInput: FormInputProps = useMemo(
    () => ({
      name: asCheckboxInputName,
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: t('label.asCheckbox'),
      labelPlacement: 'end',
    }),
    [asCheckboxInputName, t]
  )

  const asDurationInput: FormInputProps = useMemo(
    () => ({
      name: asDurationInputName,
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: t('label.asDuration'),
      labelPlacement: 'end',
    }),
    [asDurationInputName, t]
  )

  const timeUnitInput: FormInputProps = {
    name: timeUnitName,
    inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
    label: t('searchFilterForm.timeUnit.label'),
    placeholder: t('searchFilterForm.timeUnit.placeholder'),
    autocompleteOptions: [
      { id: ETimeUnit.MONTH, label: ETimeUnit.MONTH },
      { id: ETimeUnit.DAY, label: ETimeUnit.DAY },
      { id: ETimeUnit.HOUR, label: ETimeUnit.HOUR },
      { id: ETimeUnit.MINUTE, label: ETimeUnit.MINUTE },
      { id: ETimeUnit.SECOND, label: ETimeUnit.SECOND },
    ],
    rules: {
      required: true,
    },
  }

  return {
    valueInput,
    valueTypeInput,
    asCheckboxInput,
    asDurationInput,
    fieldTypeInput,
    timeIncrementInput,
    minInput,
    maxInput,
    timeUnitInput,
    defaultPlaceholderInput,
    formatDateInput,
    placeholderValueInput,
    placeholderStartInput,
    placeholderEndInput,
    dropDownListInput,
    dropDownWindowInput,
  }
}
